import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { login, getTenantDisplayName } from '../services/authService';
import { handlePostLoginRedirect } from '../services/clientService';
import { TURNSTILE_SITE_KEY } from '../config/env';
import './Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileInstance>(null);
  const navigate = useNavigate();

  const tenantName = getTenantDisplayName();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!turnstileToken) {
      setError('Por favor complete la verificación de seguridad');
      return;
    }

    setLoading(true);

    try {
      await login(username, password, turnstileToken);
      handlePostLoginRedirect();
    } catch (err) {
      const loginErr = err as Error & { code?: string };
      if (
        loginErr.code === 'email_not_verified' ||
        loginErr.code === 'user_is_not_verified'
      ) {
        navigate(`/verify-email?email=${encodeURIComponent(username)}`, {
          state: { autoResend: true },
        });
        return;
      }
      setError(loginErr.message || 'Usuario o contraseña incorrectos. Por favor, intente nuevamente.');
      console.error('Login error:', err);
      // Reset turnstile on error so the user can get a fresh token
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Get the current origin for the redirect URI
     const redirectUri = import.meta.env.VITE_REDIRECT_SIGN_IN;
    window.location.href = `https://login.clinarya.com/oauth2/authorize?identity_provider=Google&client_id=v3v0ktmid8h51chq7r4nqv7u3&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=email+openid+phone+profile`;
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleRegister = () => {
    navigate('/register');
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1 className="login-title">{tenantName.toUpperCase()}</h1>
          <h2 className="login-subtitle">Portal de Administración</h2>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">E-mail</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Email"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ingrese su contraseña"
              required
              disabled={loading}
              className="form-input"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-group turnstile-wrapper">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onError={() => setTurnstileToken('')}
              onExpire={() => setTurnstileToken('')}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="login-button"
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        <div className="divider">
          <span>O</span>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          className="google-button"
        >
          <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continuar con Google
        </button>

        <div className="login-footer">
          <button
            type="button"
            onClick={handleForgotPassword}
            className="link-button"
          >
            ¿Olvidó su contraseña?
          </button>
          <span className="footer-separator">•</span>
          <button
            type="button"
            onClick={handleRegister}
            className="link-button"
          >
            Crear cuenta
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
